import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ProOnly } from '../label/ProOnly';
import { useClasses } from '../hooks';
import { NekoSpinner } from '../misc/Spinner';

import { Icon } from '@iconify/react';
import checkboxBlankCircleOutline from '@iconify/icons-mdi/checkbox-blank-circle-outline';
import recordCircle from '@iconify/icons-mdi/record-circle';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import chevronUp from '@iconify/icons-mdi/chevron-up';
import { NekoIcon } from '../misc/Icon';
import { NekoInput } from './Input';
import { NekoContextMenu } from '../misc/ContextMenu';

import { Select, SelectedOption, Description, Options, OptionsContainer,
  RadioOption, SelectOption } from './Select.styles';

const StyledNekoSelect = props => {
  const { name, value, scrolldown = false, isPro = false, onChange, className, disabled, isBusy = false,
    description: inDescription, chevronIconSize = 24, textFiltering, requirePro: inRequirePro, ...rest } = props;
  const currentOption = React.Children.toArray(props.children)
    .find(child => child.props.value === value || child.props.checked)?.props;
  const label = currentOption?.label || 'Select';
  const description = currentOption?.description || inDescription;
  const requirePro = currentOption?.requirePro || false;
  const icon = currentOption?.icon;
  const iconSize = currentOption?.iconSize;
  const [ showOptions, toggleOptions ] = useState(false);
  const [ filterText, setFilterText ] = useState('');
  const componentRef = useRef();
  const showUpProOnly = requirePro && !isPro;

  const onContextMenuClose = () => {
    if (showOptions) {
      toggleOptions(false);
    }
  }

  const onOptionClick = (newValue) => {
    if (newValue !== value) {
      if (!onChange) {
        console.log(`The onChange handler it not set for this checkbox.`, props);
        return;
      }
      onChange(newValue, name);
    }
    if (scrolldown) {
      toggleOptions(false);
    }
  }

  const children = React.Children.map(props.children, child => {
    if ( !child ) return null;
    return React.cloneElement(child, {
      name: child.props.name || name,
      checked: child.props.value === value || child.props.checked,
      onClick: onOptionClick,
      scrolldown,
      isPro,
      disabled
    });
  });

  const filteredChildren = useMemo(() => {
    if (!filterText || !children.length) return children;

    const filtered = [];
    React.Children.forEach(children, child => {
      if (child.props.label.includes(filterText) || child.props.value.includes(filterText)) {
        filtered.push(child);
      }
    });
    return filtered;
  }, [children, filterText]);

  const selectClassNames = useClasses('neko-select', className,{ 
    'show-options': showOptions,
    disabled: disabled || isBusy
  });
  const selectOptionsClassNames = useClasses('neko-select-options', { 
    hidden: !showOptions,
    //'neko-dropdown-up': dropdownDirection === 'up',
    //'neko-dropdown-down': dropdownDirection === 'down',
  });
  const selectOptionClassNames = useClasses('neko-select-option', { isBusy });

  return scrolldown ? <Select name={name} {...rest}
        onClick={() => toggleOptions(!showOptions)}
        className={selectClassNames}
        data-is-disabled={disabled || isBusy}>
        <SelectedOption className={selectOptionClassNames} ref={componentRef}>
          {isBusy ? <><NekoSpinner type="circle" size="20px" /></> : <>
            {icon && <NekoIcon icon={icon} width={iconSize} height={iconSize} style={{ marginRight: `${Math.max(iconSize - 15, 4)}px` }} />}
            <span className='neko-select-option-label'>{label}</span>
            <div className="rightContent">
              {showUpProOnly && <ProOnly />}
              <Icon icon={showOptions ? chevronUp : chevronDown} width={chevronIconSize} />
            </div>
          </>}
        </SelectedOption>
        {description && <Description>{description}</Description>}

        <NekoContextMenu visible={showOptions} targetRef={componentRef} onClose={onContextMenuClose}>
          <Options className={selectOptionsClassNames}>
            {textFiltering && 
              <OptionsContainer className='neko-select-filter-container'>
                <NekoInput value={filterText} placeholder="Search..." onChange={(v) => setFilterText(v)}
                  onClick={(e) => e.stopPropagation()} style={{ background: `var(--neko-white)` }} inputStyle={{ margin: 0, borderRadius: 0 }} autoFocus />
              </OptionsContainer>
            }
            <OptionsContainer>{[filteredChildren]}</OptionsContainer>
          </Options>
        </NekoContextMenu>

      </Select>
    : children;
};

const StyledNekoOption = props => {
  const { id, name, value, checked, icon, label, description, onClick, scrolldown, isPro = false,
    requirePro = false, optionDisabled = false, iconSize = 20 } = props;

  const showUpProOnly = requirePro && !isPro;
  const classNames = useClasses({'neko-radio': !scrolldown}, {'neko-select-option': scrolldown}, props.className, { 'disabled': showUpProOnly || optionDisabled });

  const radioOption = (
    <RadioOption className={classNames} onClick={() => onClick(value, name)}>
      <label htmlFor={id}>
        <Icon icon={checked ? recordCircle : checkboxBlankCircleOutline} width="24px" color={showUpProOnly ? `var(--neko-disabled-color)` : `var(--neko-main-color)`}/>
        <div className="inner-container">
          <span className="label">{label}<ProOnly className="inline" style={{ top: -1 }} show={showUpProOnly} /></span>
          {description ? <small className="description">{description}</small> : null}
        </div>
      </label>
    </RadioOption>
  );

  const selectOption = (
    <SelectOption className={classNames} onClick={() => onClick(value, name)}>
      <div className="option">
        <div className="option-group">
          {icon && (<NekoIcon icon={icon} width={iconSize} height={iconSize}
              style={{ marginRight: `${Math.max(iconSize - 11, 4)}px` }} />
          )}
          {label}
        </div>
        <ProOnly show={showUpProOnly} />
      </div>
    </SelectOption>
  );
  
  return scrolldown ? selectOption : radioOption;
};

/**
 * The NekoSelect has 2 types, radio (default) or select.
 */
const NekoSelect = (props) => {
  return (<StyledNekoSelect {...props} />);
}

NekoSelect.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  scrolldown: PropTypes.bool,
  isPro: PropTypes.bool,
  onChange: PropTypes.func,
  isBusy: PropTypes.bool,
  chevronIconSize: PropTypes.number,
  textFiltering: PropTypes.bool,
};

NekoSelect.defaultProps = {
  name: undefined,
  description: undefined,
  scrolldown: false,
  isPro: false,
  onChange: undefined,
  isBusy: false,
  chevronIconSize: 24,
  textFiltering: undefined,
};

const NekoOption = (props) => {
  return (<StyledNekoOption {...props} />);
}

NekoOption.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  checked: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  onClick: PropTypes.func,
  scrolldown: PropTypes.bool,
  isPro: PropTypes.bool,
  optionDisabled: PropTypes.bool,
  requirePro: PropTypes.bool,
  icon: PropTypes.string,
  iconSize: PropTypes.number,
};

NekoOption.defaultProps = {
  id: undefined,
  name: undefined,
  value: undefined,
  checked: false,
  label: undefined,
  description: undefined,
  onClick: undefined,
  scrolldown: false,
  isPro: false,
  optionDisabled: false,
  requirePro: false,
  icon: undefined,
  iconSize: 20,
};

export { NekoSelect, NekoOption };
